
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import { Dispatch, useEffect } from "react";
import { ConnectedProps, connect, useDispatch, useSelector } from "react-redux";
import { Col, Row, Spinner } from "reactstrap";
import { AnyAction } from "redux";
import { ICategory, IProject, IProjectReport, IPublicStatistics, ISDG, ProjectState } from "@api/schema";
import BaseLayout from "@components/BaseLayout";
import PageError from "@components/common/PageError";
import { MarketInfoCardType } from "@components/marketplace/MarketInfoCard";
import MarketSearchForm from "@components/marketplace/MarketSearchForm";
import ProjectScroller from "@components/marketplace/projects/ProjectScroller";
import { addNotificationAction } from "@redux/actions/notifications";
import { IFormikActions, loadStatisticsAction, newLoadCollectionAction, newLoadCollectionPageAction } from "@redux/helper/actions";
import { IStatisticsSingleState } from "@redux/helper/reducers";
import { AppState } from "@redux/reducer";
import { selectCurrentUser } from "@redux/reducer/auth";
import { selectCollectionUsecaseState } from "@redux/reducer/data";
import { EntityType, ScopeTypes, StatisticsType } from "@redux/reduxTypes";
import { IMarketSearchParams, MarketProjectFilterType, loadMarketplaceProjectsUsecaseAction, reportProjectAction, selectLoadedMarketplaceProjectsUsecaseState, setMarketSearchParamsAction } from "@redux/usecases/marketplace";
import { getIntFromQuery } from "@services/routes";
import { idExistsInEntities } from "@services/util";
const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
    addNotification: (message: string) => dispatch(addNotificationAction(message, "success")),
    loadMarketProjects: (marketSearchState: IMarketSearchParams) => {
        // do not filter for categories and sdgs when filtering for ideas
        if (marketSearchState.projectTypeFilter.includes(MarketProjectFilterType.Ideas)) {
            marketSearchState.categories = [];
            marketSearchState.sdgs = [];
        }
        dispatch(loadMarketplaceProjectsUsecaseAction(marketSearchState));
    },
    loadNextMarketPage: (url: string, scope: string) => dispatch(newLoadCollectionPageAction(EntityType.Project, url, scope)),
    reportProject: (project: IProject, report: IProjectReport, actions: IFormikActions) => dispatch(reportProjectAction(project, report, actions)),
    setMarketSearchParams: (params: IMarketSearchParams) => dispatch(setMarketSearchParamsAction(params)),
    loadPublicStatistics: () => dispatch(loadStatisticsAction(StatisticsType.Platform))
});
const mapStateToProps = (state: AppState) => ({
    projectsState: selectLoadedMarketplaceProjectsUsecaseState(state),
    marketSearchState: state.marketplaceSearchState,
    publicStatistics: state.statistics[StatisticsType.Platform] as IStatisticsSingleState<IPublicStatistics>,
    currentUser: selectCurrentUser(state),
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type PageProps = ConnectedProps<typeof connector>;
/**
 * this page shows the public marketplace
 *
 * @todo: this page is re-rendered many times: how to reduce this?
 *
 */
const MarketPage: React.FC<PageProps> = (props: PageProps) => {
    const { projectsState, marketSearchState, publicStatistics, currentUser } = props;
    const { t } = useTranslation("common");
    const dispatch = useDispatch(); // to dispatch call of projectsState.getLoadNextPageAction
    // get category and SDG from a possible url-parameter
    const router = useRouter();
    const initialCategoryId = getIntFromQuery(router, "category");
    const initialSDGId = getIntFromQuery(router, "sdg");
    // @todo multimandant: filter categories for current process
    const categoriesState = useSelector((state: AppState) => selectCollectionUsecaseState(state, EntityType.Category, ScopeTypes.CategoriesLoading));
    const categories = categoriesState?.getItems<ICategory>();
    const sdgsState = useSelector((state: AppState) => selectCollectionUsecaseState(state, EntityType.Sdgs, ScopeTypes.PlatformOperation));
    const sdgs = sdgsState?.getItems<ISDG>();
    /**
     * @returns the current marketSearchState.projectTypeFilter reduced by Ideas
     */
    const currentProjectTypeFilterReducedByIdeas = (): MarketProjectFilterType[] => {
        const newProjectTypeFilter: MarketProjectFilterType[] = marketSearchState.projectTypeFilter || [];
        // "repair": if no projectTypeFilter is set, set all Project-Filter-Types
        if (newProjectTypeFilter.length === 0) {
            newProjectTypeFilter.push(...[MarketProjectFilterType.ProjectsFinished, MarketProjectFilterType.ProjectsInPlanning, MarketProjectFilterType.ProjectsRunning]);
        }
        if (newProjectTypeFilter.includes(MarketProjectFilterType.Ideas)) {
            newProjectTypeFilter.slice(newProjectTypeFilter.indexOf(MarketProjectFilterType.Ideas), 1);
        }
        return newProjectTypeFilter;
    };
    // #region useEffects: initial useEffect as well as react on changing incoming data
    useEffect(() => {
        // initial loadings when accessing the page for the first time
        // if no statistics are given, load them
        if (!publicStatistics?.loaded) {
            props.loadPublicStatistics();
        }
        // if no categories are given, load them
        if (!categoriesState.loaded) {
            dispatch(newLoadCollectionAction(EntityType.Category, {}, ScopeTypes.CategoriesLoading, true));
        }
        // if no SDGs are given, load them
        if (!sdgsState.loaded) {
            dispatch(newLoadCollectionAction(EntityType.Sdgs, null, ScopeTypes.PlatformOperation, true));
        }
    }, []);
    useEffect(() => {
        // if initialCategoryId is given and valid and not already set, set it as filter
        if (idExistsInEntities(initialCategoryId, categories) && marketSearchState.categories?.toString() !== initialCategoryId.toString()) {
            // exclude ideas from projectTypeFilter, because they dont have Categories
            const newProjectTypeFilter = currentProjectTypeFilterReducedByIdeas();
            const params: IMarketSearchParams = {
                categories: [initialCategoryId],
                sdgs: null,
                pattern: null,
                sortBy: marketSearchState.sortBy,
                sortOrder: marketSearchState.sortOrder,
                projectTypeFilter: newProjectTypeFilter,
            };
            props.setMarketSearchParams(params);
        }
        // if initialSDGId is given and valid  and not already set, set it as filter
        if (idExistsInEntities(initialSDGId, sdgs) && marketSearchState.sdgs?.toString() !== initialSDGId.toString()) {
            // exclude ideas from projectTypeFilter, because they dont have SDGs
            const newProjectTypeFilter = currentProjectTypeFilterReducedByIdeas();
            const params: IMarketSearchParams = {
                sdgs: [initialSDGId],
                categories: null,
                pattern: null,
                sortBy: marketSearchState.sortBy,
                sortOrder: marketSearchState.sortOrder,
                projectTypeFilter: newProjectTypeFilter
            };
            props.setMarketSearchParams(params);
        }
    }, [initialCategoryId, initialSDGId, categoriesState.loaded, sdgsState.loaded]);
    useEffect(() => {
        // set the search params to "ideas only", if there are no active projects at the platform at all
        if (publicStatistics?.data?.activeProjects === 0) {
            props.setMarketSearchParams({
                ...marketSearchState,
                projectTypeFilter: [MarketProjectFilterType.Ideas],
                pattern: null,
            });
        }
    }, [publicStatistics]);
    // #endregion
    // #region useEffects: refresh the state after logout
    // after logout the state is cleared and the user is redirected to the marketplace
    // therefor the state has to be "refilled" by necessary data
    useEffect(() => {
        // reload after cleared state after logout
        // if no categories are given, load them
        if (!categoriesState.loaded) {
            dispatch(newLoadCollectionAction(EntityType.Category, {}, ScopeTypes.CategoriesLoading, true));
        }
    }, [categoriesState.loaded]);
    useEffect(() => {
        // reload after cleared state after logout
        // if no SDGs are given, load them
        if (!sdgsState.loaded) {
            dispatch(newLoadCollectionAction(EntityType.Sdgs, null, ScopeTypes.PlatformOperation, true));
        }
    }, [sdgsState.loaded]);
    useEffect(() => {
        // if no statistics are given, load them
        if (!publicStatistics?.loaded) {
            props.loadPublicStatistics();
        }
    }, [publicStatistics?.loaded]);
    // #endregion
    // if the user is logged in and creates a new project, the marketplace-projects must be refreshed to show it to him
    useEffect(() => {
        if (!projectsState.loaded && currentUser) {
            props.loadMarketProjects(marketSearchState);
        }
    }, [currentUser?.createdProjects]);
    // (re)load market content when marketSearchState changes
    useEffect(() => {
        props.loadMarketProjects(marketSearchState);
    }, [marketSearchState]);
    /**
     * triggered on changes by the MarketSearchForm
     */
    const onSubmit = (values: IMarketSearchParams, actions?: IFormikActions): void => {
        props.setMarketSearchParams(values);
        actions?.setSubmitting(false);
    };
    return <BaseLayout pageTitle={t("page.projects.index.title")} isMarketplace={true}>
    <Row className="justify-content-md-center">
      <Col className="text-center">
        <MarketSearchForm onSubmit={onSubmit} categories={categories} sdgs={sdgs} searchParams={marketSearchState} initialCategoryId={initialCategoryId} initialSDGId={initialSDGId} publicStatistics={publicStatistics?.data}/>
        <PageError error={projectsState?.loadingError}/>
        <PageError error={publicStatistics?.loadingError}/>
      </Col>
    </Row>
    <Row>
      <Col>
        {// show spinner before the project list, to make a visual response when the user changes the filter criteria
        // when a list of project from previous search is visible
        projectsState.isLoading && <Spinner />}
      </Col>
    </Row>
    {(projectsState?.loaded) &&
            <ProjectScroller addNotification={props.addNotification} reportProject={props.reportProject} projects={projectsState.getItems<IProject>().filter(p => p.state === ProjectState.Active && !p.locked)} loadNext={() => dispatch(projectsState.getLoadNextPageAction())} hasNext={!!projectsState.nextLink} infoCard={marketSearchState.projectTypeFilter.includes(MarketProjectFilterType.Ideas) ? MarketInfoCardType.IdeaCard : MarketInfoCardType.ProjectCard}/>}
    {projectsState?.isLoading && projectsState?.itemIds.length > 3 &&
            // show spinner after the project list, to make sure, the user gets a visual response about the loading,
            // when scrolling even on longer lists
            <Row>
        <Col>
          {projectsState?.isLoading && <Spinner />}
        </Col>
      </Row>}
  </BaseLayout>;
};
const __Next_Translate__Page__18c4f51751d__ = connector(MarketPage);

    export default __appWithI18n(__Next_Translate__Page__18c4f51751d__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: false,
      loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
    });
  